import styles from './PDFTextmarks.module.scss';

import {
  Close,
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  FormatUnderlined,
  NorthWest,
  Settings,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import clsx from 'clsx';
import { type FC, type ReactNode, useState } from 'react';
import { useDrag } from 'react-dnd';

import { DndTypes } from '@work4all/utils/lib/variables';

import { PDFTextmarkFontPicker } from './components/PDFTextmarkFontPicker';
import { PDFTextmarkSizePicker } from './components/PDFTextmarkSizePicker';
import { EFontFamily, EFontStyle } from './PDFTextmarkContainer';

export interface BoxProps {
  id: string;
  left: number;
  top: number;
  hideSourceOnDrag?: boolean;
  children?: ReactNode;
  onDelete?: () => void;
  disabled?: boolean;
  fontFamily?: EFontFamily;
  onFontFamilyChange?: (value: EFontFamily) => void;
  fontStyle?: EFontStyle[];
  onFontStyleChange?: (value: EFontStyle[]) => void;
  fontSize?: number;
  onFontSizeChange?: (value: number) => void;
}

export const DragBox: FC<BoxProps> = ({
  id,
  left,
  top,
  hideSourceOnDrag = true,
  children,
  onDelete,
  disabled,
  fontFamily,
  fontStyle,
  fontSize,
  onFontFamilyChange,
  onFontSizeChange,
  onFontStyleChange,
}) => {
  const [showConf, setShowConf] = useState(false);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DndTypes.BOX,
      item: { id, left, top },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [id, left, top]
  );

  if (isDragging && hideSourceOnDrag) {
    return (
      <div
        ref={(node) => {
          drag(node);
        }}
      />
    );
  }

  return (
    <div
      className={clsx('box', styles.dragBox)}
      ref={
        disabled
          ? undefined
          : (node) => {
              drag(node);
            }
      }
      style={{ left, top }}
      data-testid="box"
    >
      <Stack
        className={styles.textContainer}
        direction="row"
        alignItems="center"
      >
        <NorthWest sx={{ fontSize: '0.75rem', alignSelf: 'start' }} />
        {children}
        {!disabled && (
          <Box pl="0.5rem">
            <IconButton
              onClick={() => setShowConf(!showConf)}
              size="small"
              className={styles.button}
            >
              <Settings className={styles.icon} />
            </IconButton>
            <IconButton
              onClick={onDelete}
              size="small"
              className={styles.button}
            >
              <Close className={styles.icon} />
            </IconButton>
          </Box>
        )}
      </Stack>
      {showConf && (
        <Stack direction="row" alignItems="center">
          <PDFTextmarkFontPicker
            fontFamily={fontFamily}
            onFontFamilyChange={onFontFamilyChange}
          />
          <PDFTextmarkSizePicker
            fontSize={fontSize}
            onFontSizeChange={onFontSizeChange}
          />
          <ToggleButtonGroup
            sx={{ height: '1.75rem' }}
            value={fontStyle}
            onChange={(e, value) => onFontStyleChange?.(value)}
          >
            <ToggleButton value={EFontStyle.Bold}>
              <FormatBold className={styles.icon} />
            </ToggleButton>
            <ToggleButton value={EFontStyle.Italic}>
              <FormatItalic className={styles.icon} />
            </ToggleButton>
            <ToggleButton value={EFontStyle.Underline}>
              <FormatUnderlined className={styles.icon} />
            </ToggleButton>
            <ToggleButton value={EFontStyle.Strikeout}>
              <FormatStrikethrough className={styles.icon} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}
    </div>
  );
};
